import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useListServersQuery } from '../store/api';
import { logOut } from '../store/authSlice';
import dayjs from 'dayjs';
import {
  SearchOutlined,
  BellOutlined,
  ExpandOutlined,
  SettingOutlined,
  LogoutOutlined,
  UserOutlined,
  CloudServerOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  MenuOutlined,
} from '@ant-design/icons';
import { Dropdown } from 'antd';

const pageTitles = {
  '/dashboard': 'Dashboard',
  '/': 'Dashboard',
  '/lista': 'Servidores',
  '/clientes': 'Clientes',
  '/proyectos': 'Proyectos',
  '/proyecto': 'Proyectos — Infraestructura',
  '/servicios': 'Servicios',
  '/historial': 'Historial de Cargas',
  '/metricas': 'Métricas de Servidor',
  '/ollama': 'IA SMART Monitor',
  '/agregar': 'Agregar Servidor',
};

const Header = ({ onMobileMenuToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef(null);

  const { data: servers } = useListServersQuery(undefined, { pollingInterval: 60000 });

  // Compute alerts from servers
  const alerts = React.useMemo(() => {
    if (!servers) return [];
    const now = dayjs();
    const items = [];

    servers.forEach((srv) => {
      const carga = parseFloat((srv.carga || '').split(',')[0]);
      if (carga > (srv.limitealerta || 1.5)) {
        items.push({
          id: `carga-${srv.id}`,
          type: 'warning',
          icon: <WarningOutlined />,
          title: srv.nombre,
          detail: `Carga alta: ${srv.carga}`,
          time: srv.fechaupdatedsincro,
        });
      }

      if (!srv.fechaupdatedsincro || now.diff(dayjs(srv.fechaupdatedsincro), 'minute') > 10) {
        items.push({
          id: `offline-${srv.id}`,
          type: 'danger',
          icon: <CloudServerOutlined />,
          title: srv.nombre,
          detail: 'Sin reporte por más de 10 min',
          time: srv.fechaupdatedsincro,
        });
      }
    });
    return items;
  }, [servers]);

  const currentTitle = pageTitles[location.pathname] || 'Dashboard';
  const isEditPage = location.pathname.startsWith('/editar');
  const displayTitle = isEditPage ? 'Editar Servidor' : currentTitle;

  const handleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  const handleLogout = () => {
    dispatch(logOut());
    window.location.href = '/';
  };

  useEffect(() => {
    if (searchOpen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [searchOpen]);

  const notificationContent = (
    <div className="notification-dropdown">
      <div className="notification-header">
        <h4>Notificaciones</h4>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          {alerts.length} alertas
        </span>
      </div>
      <div className="notification-list">
        {alerts.length === 0 ? (
          <div style={{ padding: '24px 20px', textAlign: 'center' }}>
            <CheckCircleOutlined style={{ fontSize: 28, color: 'var(--success)', marginBottom: 8 }} />
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              No hay alertas activas
            </div>
          </div>
        ) : (
          alerts.slice(0, 8).map((alert) => (
            <div key={alert.id} className="notification-item">
              <div
                style={{
                  width: 32, height: 32, borderRadius: 8,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: alert.type === 'danger' ? 'var(--danger-light)' : 'var(--warning-light)',
                  color: alert.type === 'danger' ? 'var(--danger)' : 'var(--warning)',
                  fontSize: 14, flexShrink: 0,
                }}
              >
                {alert.icon}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-default)' }}>
                  {alert.title}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  {alert.detail}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      {alerts.length > 0 && (
        <div
          style={{
            padding: '10px 20px', borderTop: '1px solid var(--border)',
            textAlign: 'center',
          }}
        >
          <button className="view-all-link" style={{ margin: '0 auto' }} onClick={() => navigate('/dashboard')}>
            Ver todas las alertas →
          </button>
        </div>
      )}
    </div>
  );

  const userMenuItems = [
    { key: 'profile', label: 'Mi Perfil', icon: <UserOutlined /> },
    { key: 'settings', label: 'Configuración', icon: <SettingOutlined /> },
    { type: 'divider' },
    { key: 'logout', label: 'Cerrar Sesión', icon: <LogoutOutlined />, danger: true },
  ];

  const handleUserMenu = ({ key }) => {
    if (key === 'logout') handleLogout();
  };

  return (
    <div className="app-header">
      <div className="header-left">
        <button
          type="button"
          className="header-hamburger"
          onClick={onMobileMenuToggle}
          aria-label="Abrir menú"
        >
          <MenuOutlined />
        </button>
        <div className="header-breadcrumb">
          <span>Páginas / </span>
          <strong>{displayTitle}</strong>
        </div>
      </div>

      <div className="header-right">
        {/* Search */}
        <div className="header-search">
          <SearchOutlined />
          <input
            ref={searchRef}
            placeholder="Buscar..."
            onFocus={() => setSearchOpen(true)}
            onBlur={() => setSearchOpen(false)}
          />
        </div>

        {/* Fullscreen */}
        <button className="header-icon-btn" onClick={handleFullscreen} title="Pantalla completa">
          <ExpandOutlined />
        </button>

        {/* Notifications */}
        <Dropdown
          dropdownRender={() => notificationContent}
          trigger={['click']}
          placement="bottomRight"
        >
          <button className="header-icon-btn">
            <BellOutlined />
            {alerts.length > 0 && (
              <span className="header-notification-count">
                {alerts.length > 9 ? '9+' : alerts.length}
              </span>
            )}
          </button>
        </Dropdown>

        {/* User */}
        <Dropdown menu={{ items: userMenuItems, onClick: handleUserMenu }} trigger={['click']} placement="bottomRight">
          <div className="header-user">
            <div className="header-user-avatar">
              <UserOutlined />
            </div>
            <div className="header-user-info">
              <div className="header-user-name">Admin</div>
              <div className="header-user-role">Administrador</div>
            </div>
          </div>
        </Dropdown>
      </div>
    </div>
  );
};

export default Header;
